import { useState } from "react";
import { Clock, Download, Eye, Loader2, RotateCcw, X } from "lucide-react";
import { documentApi } from "../api";
import { useRestoreDocument } from "../hooks";
import { DocumentAccessLogModal } from "./DocumentAccessLogModal";
import { DocumentPreviewModal, describeError } from "./DocumentPreviewModal";
import type { StoredDocument } from "../types";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes?: number | null) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmt(value?: string | null) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString("en-KE", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch { return value; }
}

const EXT_ICON: Record<string, string> = {
  pdf: "📄", doc: "📝", docx: "📝",
  xls: "📊", xlsx: "📊",
  png: "🖼", jpg: "🖼", jpeg: "🖼",
  zip: "🗜", rar: "🗜",
};

function fileIcon(doc: StoredDocument): string {
  const ext = (doc.fileExtension ?? doc.originalFileName.split(".").pop() ?? "").toLowerCase();
  return EXT_ICON[ext] ?? "📎";
}

const VISIBILITY_CLS: Record<string, string> = {
  INTERNAL:     "bg-slate-100 text-slate-600",
  RESTRICTED:   "bg-amber-100 text-amber-700",
  CONFIDENTIAL: "bg-red-100 text-red-700",
};

// ── Component ─────────────────────────────────────────────────────────────────

type Props = {
  documents: StoredDocument[];
  /** Show the "access log" (who viewed/downloaded) action — HR_DOCUMENT_MANAGE / SUPER_ADMIN only. */
  canViewAccessLog?: boolean;
  /** Show "Restore" on soft-deleted rows — HR_DOCUMENT_MANAGE / SUPER_ADMIN only, no time limit. */
  canRestore?: boolean;
  emptyMessage?: string;
};

// NOTE: there is intentionally no generic "Delete" action here anymore — the backend
// removed DELETE /api/hr/documents/{id} so a document can't be deleted without going
// through the owning module's own endpoint (which enforces that module's business rules,
// e.g. a closed claim can't lose its receipt). Delete a document from the record it's
// attached to (claim, training request, requisition, etc.) instead. Restore is unaffected
// — it's still generic/admin-only, since undoing a delete never bypasses anything.

export function DocumentViewer({
  documents,
  canViewAccessLog = false,
  canRestore = false,
  emptyMessage,
}: Props) {
  const restore = useRestoreDocument();
  const [logTarget,     setLogTarget]     = useState<StoredDocument | null>(null);
  const [previewTarget, setPreviewTarget] = useState<StoredDocument | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  if (documents.length === 0) {
    return (
      <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-500">
        {emptyMessage ?? "No documents attached."}
      </p>
    );
  }

  async function handleDownload(doc: StoredDocument) {
    setDownloadError(null);
    setDownloadingId(doc.id);
    try {
      const { blob, filename } = await documentApi.downloadBlob(doc.id);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = doc.originalFileName || filename || "document";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      setDownloadError(describeError(err, doc.originalFileName));
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="space-y-2">
      {documents.map((doc) => {
        const isDeleted = doc.status === "DELETED";
        return (
        <div
          key={doc.id}
          className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${
            isDeleted ? "border-red-100 bg-red-50/30" : "border-slate-200 bg-white"
          }`}
        >
          {/* Icon */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-lg">
            {fileIcon(doc)}
          </div>

          {/* Meta */}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-slate-800">
              {doc.originalFileName}
            </p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
              {doc.documentType && (
                <span className="font-medium text-slate-500">{doc.documentType}</span>
              )}
              {doc.fileSizeBytes ? <span>{formatBytes(doc.fileSizeBytes)}</span> : null}
              {doc.uploadedAt ? <span>{fmt(doc.uploadedAt)}</span> : null}
              {doc.uploadedByName ? <span>by {doc.uploadedByName}</span> : null}
              {doc.employeeName && <span>· {doc.employeeName}</span>}
              {doc.departmentName && <span>· {doc.departmentName}</span>}
              {(doc.viewCount || doc.downloadCount) ? (
                <span className="inline-flex items-center gap-1 text-slate-400">
                  <Clock className="h-3 w-3" />
                  {doc.viewCount ?? 0} view{(doc.viewCount ?? 0) !== 1 ? "s" : ""} · {doc.downloadCount ?? 0} download{(doc.downloadCount ?? 0) !== 1 ? "s" : ""}
                </span>
              ) : null}
              {doc.visibility && doc.visibility !== "INTERNAL" && (
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${VISIBILITY_CLS[doc.visibility] ?? "bg-slate-100 text-slate-600"}`}>
                  {doc.visibility}
                </span>
              )}
              {isDeleted && (
                <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
                  Deleted{doc.deletedByName ? ` by ${doc.deletedByName}` : ""}{doc.deletedAt ? ` · ${fmt(doc.deletedAt)}` : ""}
                </span>
              )}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPreviewTarget(doc)}
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              title="View"
            >
              <Eye className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={() => handleDownload(doc)}
              disabled={downloadingId === doc.id}
              className="rounded-xl p-2 text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
              title="Download"
            >
              {downloadingId === doc.id
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />}
            </button>

            {canViewAccessLog && (
              <button
                type="button"
                onClick={() => setLogTarget(doc)}
                className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                title="Access log — who viewed/downloaded this"
              >
                <Clock className="h-4 w-4" />
              </button>
            )}

            {isDeleted && canRestore && (
              <button
                type="button"
                onClick={() => restore.mutate(doc.id)}
                disabled={restore.isPending}
                className="rounded-xl p-2 text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50"
                title="Restore"
              >
                {restore.isPending
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <RotateCcw className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
        );
      })}

      {downloadError && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{downloadError}</span>
          <button
            type="button"
            onClick={() => setDownloadError(null)}
            className="shrink-0 rounded-lg p-1 hover:bg-red-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {previewTarget && (
        <DocumentPreviewModal document={previewTarget} onClose={() => setPreviewTarget(null)} />
      )}

      {canViewAccessLog && (
        <DocumentAccessLogModal document={logTarget} onClose={() => setLogTarget(null)} />
      )}
    </div>
  );
}
